import {
	BusyIndicator,
	ChangeTriggerKind,
	FormattedText,
	Icon,
	FontIconName
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	listSubjects,
	listItemsBySubject,
	search,
	VilansKickSubject,
	VilansKickItem,
	VilansKickItemKind
} from "client/Api/Workspace/LiveInformation/VilansKick/vilansKickLiveInformation";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { ListItem } from "../ListItem";
import { LiveTileContent } from "../LiveTileContent";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { TileContext } from "../TileContext";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

type LoadingState<TItem> =
	{
		kind: "loading";
	}
	|
	{
		kind: "success";
		items: readonly TItem[];
	}
	|
	{
		kind: "failed";
	};

type SubjectLoadingState = LoadingState<VilansKickSubject>;
type SearchLoadingState = LoadingState<VilansKickItem>;
type SubjectWorkItemsLoadingState = LoadingState<VilansKickItem>;
type VilansKickItemContent = {
	title: string;
	subTitle?: string | null;
};

const MINIMAL_AMOUNT_SEARCH_CHARACTERS = 3;

export type VilansKickLiveTileContentProps = {
	isConfiguredCorrectly: boolean;
	connectionId: number;
	destinationWindow: DestinationWindow;
	onNavigation(): void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const VilansKickLiveTileContent = (props: VilansKickLiveTileContentProps): ReactElement => {
	const {
		isConfiguredCorrectly,
		connectionId,
		destinationWindow,
		onNavigation,
		onOpenTileMenu
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({ value: "", version: newGuid()});
	const [subjectLoadingState, setSubjectLoadingState] = useState<SubjectLoadingState>({ kind: "loading" });
	const [searchLoadingState, setSearchLoadingState] = useState<SearchLoadingState>({ kind: "loading" });
	const [subjectWorkItemsLoadingState, setSubjectWorkItemsLoadingState] = useState<SubjectWorkItemsLoadingState>({ kind: "loading" });
	const [selectedSubjectId, setSelectedSubjectId] = useState<string | undefined>();

	const {
		environmentAppId,
		icon,
		name,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	useEffect(
		() => {
			if (!isConfiguredCorrectly) {
				return undefined;
			}

			const load = async (signal: AbortSignal): Promise<void> => {
				setSubjectLoadingState({
					kind: "loading"
				});
				try {
					const subjects: readonly VilansKickSubject[] = await listSubjects(connectionId, signal);
					setSubjectLoadingState({
						kind: "success",
						items: subjects
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setSubjectLoadingState({
						kind: "failed"
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[isConfiguredCorrectly, connectionId]
	);

	useEffect(
		() => {
			if (!isConfiguredCorrectly) {
				return undefined;
			}

			if (selectedSubjectId || searchTerm.value.length < MINIMAL_AMOUNT_SEARCH_CHARACTERS) {
				setSearchLoadingState({ kind: "loading" });
				return undefined;
			}

			const load = async (
				connectionId: number,
				searchTerm: string,
				signal: AbortSignal
			): Promise<void> => {
				setSearchLoadingState({
					kind: "loading"
				});
				try {
					const items: readonly VilansKickItem[] = await search(connectionId, searchTerm, signal);
					setSearchLoadingState({
						kind: "success",
						items
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setSearchLoadingState({
						kind: "failed"
					});
				}
			};

			const cancellation = new AbortController();
			load(connectionId, searchTerm.value, cancellation.signal);
			return () => cancellation.abort();
		},
		[isConfiguredCorrectly, connectionId, selectedSubjectId, searchTerm]
	);

	useEffect(
		() => {
			if (!isConfiguredCorrectly) {
				return undefined;
			}

			if (!selectedSubjectId) {
				setSubjectWorkItemsLoadingState({ kind: "loading" });
				return undefined;
			}

			const load = async (
				connectionId: number,
				selectedSubjectId: string,
				signal: AbortSignal
			): Promise<void> => {
				setSubjectWorkItemsLoadingState({
					kind: "loading"
				});
				try {
					const items: readonly VilansKickItem[] = await listItemsBySubject(connectionId, selectedSubjectId, signal);

					const sortedItems: VilansKickItem[] = [...items].sort(
						(a, b) => nativeSortFunction(
							a.title,
							b.title,
							DataSourceSortDirection.Ascending
						)
					);

					setSubjectWorkItemsLoadingState({
						kind: "success",
						items: sortedItems
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					setSubjectWorkItemsLoadingState({
						kind: "failed"
					});
				}
			};

			const cancellation = new AbortController();
			load(connectionId, selectedSubjectId, cancellation.signal);
			return () => cancellation.abort();
		},
		[isConfiguredCorrectly, connectionId, selectedSubjectId]
	);

	const subjectClickHandler = (subject: VilansKickSubject): void => {
		setSelectedSubjectId(subject.id);
	};

	const itemClickHandler = (item: VilansKickItem): void => {
		addAnalyticsEvent();
		navigateTo(
			item.url,
			destinationWindow === DestinationWindow.NewWindow
		 );
	};

	const backClickHandler = (): void => {
		setSearchTerm({ value: "", version: newGuid() });
		setSelectedSubjectId(undefined);
	};

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const renderLoadingState = <TItem extends { id: string | number; }>(
		state: LoadingState<TItem>,
		icon: FontIconName,
		contentRetriever: (item: TItem) => VilansKickItemContent,
		clickHandler: (item: TItem) => void,
		clientSideFilter?: (item: TItem) => boolean
	): ReactElement => {
		switch (state.kind) {
			case "loading":
				return <BusyIndicator />;
			case "failed":
				return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
			case "success":
				let itemsToRender: readonly TItem[] = state.items;
				if (clientSideFilter) {
					itemsToRender = itemsToRender.filter(clientSideFilter);
				}

			if (itemsToRender.length === 0) {
				return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
			}

			return (
				<LiveTileContent.Scrollable>
					<LiveTileContent.List>
						{itemsToRender.map((x) => {
							const {
								title,
								subTitle
							} = contentRetriever(x);

							return (
								<ListItem
									key={x.id}
									title={title}
									isHoverable
									onClick={() => clickHandler(x)}
								>
									<ListItem.Icon>
										<Icon value={icon} />
									</ListItem.Icon>
									<ListItem.Content>
										<ListItem.Title>
											{title}
										</ListItem.Title>
										{subTitle && (
											<ListItem.Info>
												{subTitle}
											</ListItem.Info>
										)}
									</ListItem.Content>
								</ListItem>
							);
						})}
					</LiveTileContent.List>
				</LiveTileContent.Scrollable>
			);
			default:
				never(state);
		}
	};

	const renderContent = (): ReactElement => {
		if (!isConfiguredCorrectly) {
			return <LiveTileContent.Message text={localize("_IncompleteConfigurationPleaseContactYourAdmin")} />;
		}

		if (searchTerm.value && searchTerm.value.length < MINIMAL_AMOUNT_SEARCH_CHARACTERS) {
			return (
				<LiveTileContent.Message
					text={
						<FormattedText
							text={localize("_TypeAtLeastAmountCharactersToSearch")}
							thingsToReplace={[{ what: "{{Amount}}", filler: MINIMAL_AMOUNT_SEARCH_CHARACTERS }]}
						/>
					}
				/>
			);
		}

		if (!selectedSubjectId) {
			if (searchTerm.value) {
				return renderLoadingState(
					searchLoadingState,
					"file-lines",
					x => (
						{
							title: x.title,
							subTitle: getItemKindName(x.kind)
						}
					),
					itemClickHandler
				);
			} else {
				return renderLoadingState(
					subjectLoadingState,
					"layer-group",
					x => ({ title: x.title }),
					subjectClickHandler
				);
			}
		} else {
			const normalizedSearchTerm: string | undefined = normalizeForSearch(searchTerm.value);
			return renderLoadingState(
				subjectWorkItemsLoadingState,
				"file-lines",
				x => (
					{
						title: x.title,
						subTitle: getItemKindName(x.kind)
					}
				),
				itemClickHandler,
				normalizedSearchTerm
					? x => contains(x.title, normalizedSearchTerm)
					: undefined
			);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			{selectedSubjectId
				? <LiveTileContent.BackHeader onClick={backClickHandler} />
				: (
					<LiveTileContent.Header icon={icon}>
						<LiveTileContent.Title text={localize("_WorkInstructions")} />
					</LiveTileContent.Header>
				)
			}

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.SearchInput
						value={searchTerm}
						onChange={(value, version) => setSearchTerm({ value, version})}
						placeholder={localize("_Search")}
						trigger={{ kind: ChangeTriggerKind.Debounced }}
					/>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
VilansKickLiveTileContent.displayName = "VilansKickLiveTileContent";

function getItemKindName(kind: VilansKickItemKind): string | null {
	switch(kind) {
		case VilansKickItemKind.WorkInstruction:
			return localize("_WorkInstructions");
		case VilansKickItemKind.BackgroundInformation:
			return localize("_BackgroundInformation");
		case VilansKickItemKind.Unknown:
			return null;
		default:
			never(kind);
	}
};
